/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */
import React, { ReactNode } from 'react'

import { MdSignalWifiConnectedNoInternet0 } from 'react-icons/md'

import AssetTableCell from './AssetTableCell'
import {
  convertSetpoint,
  convertTemperature,
  formatDate12,
  getFridgePropColMeta,
  joinStrings,
  timeSinceFormatted,
} from './utils'

import { TemperatureUnit } from '../types'

type ValueType = 'Temperature' | 'Number' | 'String' | 'Setpoint'

type FridgePropColProps = {
  value: string | number
  fridgeSampleTime: Date
  powerState: string
  fridgeOnline: boolean
  type: ValueType
  temperatureUnit?: TemperatureUnit
}

const formatValue = (type: ValueType, value: string | number, temperatureUnit: TemperatureUnit) => {
  switch (type) {
    case 'Temperature':
      return convertTemperature(value as number, temperatureUnit)
    case 'Setpoint':
      return convertSetpoint(value as number, temperatureUnit)
    default:
      return value
  }
}

const FridgePropCol: React.FC<FridgePropColProps> = ({
  value,
  fridgeSampleTime,
  powerState,
  fridgeOnline,
  type,
  temperatureUnit = 'Metric',
}) => {
  const { isValueBlank, displayBlank, displayMissing } = getFridgePropColMeta(powerState, fridgeOnline, value)

  const lastValue = isValueBlank ? '<No reading>' : formatValue(type, value, temperatureUnit)

  const lastReportedString = fridgeSampleTime
    ? `Last value reported at ${formatDate12(fridgeSampleTime)} ${timeSinceFormatted(fridgeSampleTime)}`
    : ''
  const lastValueString = `Last value: ${lastValue}`

  let tooltip = lastReportedString
  let displayValue: ReactNode = <div className="h-4 w-full flex"></div>
  if (displayBlank) {
    tooltip = joinStrings(lastReportedString, lastValueString)
  } else if (displayMissing) {
    tooltip = joinStrings('No data available', lastReportedString, lastValueString)
    displayValue = <MdSignalWifiConnectedNoInternet0 className="w-4 h-4 text-gray-500" />
  } else {
    displayValue = formatValue(type, value, temperatureUnit)
  }

  return <AssetTableCell tooltip={tooltip}>{displayValue}</AssetTableCell>
}

export default FridgePropCol
